<template>
  <div class="subscriptions-search">


    <headline>
      {{ $tc('subscription.subscription', 2) }}
    </headline>
    <v-row >
      <v-col class="text-right">
        <v-btn small @click="$refs.subscribeModal.open()">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('subscription.add')}}
        </v-btn>
        <v-btn class="ml-3" small @click="openSyncProformaInvoiceModal">
          <v-icon left>mdi-sync</v-icon>
          {{ $t('proforma-invoice.sync-proforma-invoices')}}
        </v-btn>
        <audit-trail-button class="ml-2" entity-type="Subscription" :organization-id="$route.params.id" />
        <div v-if="lastSyncTask" class="mt-2 text-caption">
          {{ $t('proforma-invoice.sync-task.last') }} :
          {{ lastSyncTask.createdAt | formatDate($t('format_datetime')) }}
          <v-chip x-small class="mx-1" :color="syncTaskStatusColor(lastSyncTask.status)" text-color="white">
            {{ $t('proforma-invoice.sync-task.statuses.' + lastSyncTask.status) }}
          </v-chip>
          {{ lastSyncTask.counters.done + lastSyncTask.counters.failed }} / {{ lastSyncTask.counters.total }}
          <a class="ml-1" @click.prevent="$refs.syncTaskModal.open(lastSyncTask.id)">{{ $t('proforma-invoice.sync-task.see') }}</a>
        </div>
      </v-col>
    </v-row>


    <v-row class="mt-10">
      <v-col>
        <v-data-table
            class="subscriptions-list"
            :headers="headers"
            :loading="loading"
            :items="subscriptions"
            :options.sync="options"
            :server-items-length="total"
        >

          <template #item.billingCycle="{item}">
            {{ $t('enums.billing-cycle.' + item.billingCycle) }}
          </template>

          <template #item.activationStartDate="{item}">
            {{ item.activationStartDate | formatDate($t('format_date'))}}
          </template>

          <template #item.billingStartDate="{item}">
            {{ item.billingStartDate | formatDate($t('format_date'))}}
          </template>

          <template #item.commitmentDuration="{item}">
            {{ item.commitmentDuration }} {{ $t('month')}}
          </template>
          <template #item.enabled="{value}">
            <v-icon v-if="value">mdi-check</v-icon>
          </template>
          <template #item.options="{item}">
            <v-btn icon :to="{name: 'organizations.subscriptions.edit', params: {subscriptionId: item.id}}">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn @click.prevent="delSubscription(item)" small icon>
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>

        </v-data-table>

      </v-col>
    </v-row>

    <subscription-licences-list
        v-if="!loading"
        :organizationId="organization.id"
        :subscription-id="selectedSubscriptionId"
    >
      <template #filters v-if="subscriptions.length > 1">
        <v-select
          v-model="selectedSubscriptionId"
          :label="$tc('subscription.filter-by-subscription')"
          :items="subscriptions"
          item-text="subscriptionPlan.name"
          item-value="id"
          hide-details
          clearable
        >
        </v-select>
      </template>
    </subscription-licences-list>

    <organization-subscribe-plan-modal
        ref="subscribeModal"
        :organization-id="organization.id"
        @subscribed="onSubscribed"
    />

    <v-dialog
        v-model="showSyncProformaInvoiceModal"
        max-width="560"
        eager
    >
      <v-card>
        <v-card-title class="headline font-weight-bold">
          {{ $t('proforma-invoice.sync-proforma-invoices') }}
          <v-spacer />
          <v-btn icon @click="showSyncProformaInvoiceModal = false" :aria-label="$t('close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>


        <v-divider />

        <v-card-text>

          <v-alert type="info" dense outlined class="my-4">
            {{ $t('proforma-invoice.sync-info') }}
          </v-alert>

          <v-radio-group v-model="syncProformaMode" row>
            <v-radio :label="$t('proforma-invoice.sync-options.from-month')" value="from-month"></v-radio>
            <v-radio :label="$t('proforma-invoice.sync-options.all-history')" value="all"></v-radio>
          </v-radio-group>

          <date-picker
            v-if="syncProformaMode === 'from-month'"
            :label="$t('proforma-invoice.sync-options.month-label')"
            type="month"
            v-model="syncProformaInvoiceFromMonth"
          ></date-picker>

          <v-switch
              v-model="deleteNotInvoicedProformaInvoice"
              inset
              :label="$t('proforma-invoice.delete-not-invoiced-label') || 'Supprimer les proformas non facturés'"
              :hint="$t('proforma-invoice.delete-not-invoiced-hint') || 'Activez pour retirer les proformas qui n’ont pas été convertis en facture.'"
              persistent-hint
              class="mt-2"
          />

          <v-alert
              v-if="deleteNotInvoicedProformaInvoice"
              type="warning"
              dense
              outlined
              class="mt-2"
          >
            <span v-html="$t('proforma-invoice.delete-not-invoiced-warning')"></span>
          </v-alert>
        </v-card-text>

        <v-divider />

        <v-card-actions class="justify-end">
          <v-btn text small @click="showSyncProformaInvoiceModal = false">
            {{ $t('cancel') }}
          </v-btn>

          <v-btn
              color="primary"
              class="ml-2"
              small
              @click="syncProformas"
              :loading="syncProformaLoading"
              :disabled="syncProformaLoading"
          >
            <v-icon left>mdi-sync</v-icon>
            {{ $t('proforma-invoice.sync-proforma-invoices') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <sync-task-progress-modal ref="syncTaskModal" @finished="loadLastSyncTask" />

  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import SubscriptionRepository from "@repository/SubscriptionRepository";
import OrganizationSubscribePlanModal from "@/components/modals/OrganizationSubscribePlanModal.vue";
import SubscriptionLicencesList from "@/components/elements/SubscriptionLicencesList.vue";
import ProformaInvoiceRepository from "@repository/ProformaInvoiceRepository";
import SyncTaskProgressModal from "@pages/ProformaInvoice/modals/SyncTaskProgressModal.vue";
import ProformaInvoiceSyncTaskRepository from "@repository/ProformaInvoiceSyncTaskRepository";
import dayjs from "dayjs";

export default {

  components: {
    AuditTrailButton,
    OrganizationSubscribePlanModal,
    SubscriptionLicencesList,
    SyncTaskProgressModal,
  },

  data() {
    return {
      loading: false,
      showSyncProformaInvoiceModal: false,
      deleteNotInvoicedProformaInvoice: false,
      syncProformaMode: 'from-month',
      syncProformaInvoiceFromMonth: dayjs().format('YYYY-MM-DD'),
      syncProformaLoading: false,
      lastSyncTask: null,
      selectedItem: null,
      options: null,
      selectedSubscriptionId: null,
      total: 0,
      headers: [
        {text: this.$tc('subscription.subscription'), value: 'subscriptionPlan.name'},
        {text: this.$t('subscription.billing-cycle'), value: 'billingCycle'},
        // {text: this.$t('subscription.commitment-duration'), value: 'commitmentDuration'},
        {text: this.$t('subscription.activation-start-date'), value: 'activationStartDate'},
        {text: this.$t('subscription.billing-start-date'), value: 'billingStartDate'},
        {text: this.$t('enable'), value: 'enabled'},
        {value: 'options'},
      ],
      subscriptions: [],
    }
  },

    // 'commitmentDuration' => $subscription->commitmentDuration(),
    // 'price' => $subscription->price(),
    // 'refundHandling' => $subscription->refundHandling(),
    // 'billingStartDate' => $subscription->billingStartDate(),
    // 'activationStartDate' => $subscription->activationStartDate(),

  mounted() {
    this.loadLastSyncTask()
  },

  watch: {

    options() {
      this.loadData()
    },

    subscriptions(subscriptions) {
      if(this.selectedSubscriptionId) {
        if(!subscriptions.find(item => item.id === this.selectedSubscriptionId)) {
          this.selectedSubscriptionId = null
        }
      }
    },

  },

  computed: {
    organization() {
      return this.$parent.organization ?? null
    }
  },

  methods: {
    loadData() {

      let data = {
        organizationId: this.organization?.id
      }

      if (this.options.itemsPerPage > -1) {
        data.offset = (this.options.page * this.options.itemsPerPage) - this.options.itemsPerPage
      }
      data.limit = this.options.itemsPerPage


      if (this.options.sortBy.length > 0) {
        data.orderBy = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0) {
        data.orderDirection = this.options.sortDesc[0] ? 'desc' : 'asc'
      }

      this.loading = true
      SubscriptionRepository.search(data).then(response => {
        this.total = response.total
        this.subscriptions = response.items
        this.loading = false
      }).catch(e => {
        this.notifyError(e)
        this.loading = false
      })
    },

    onSubscribed(item) {
      this.loadData()
      this.$parent.loadTabs()
    },

    delSubscription(item) {
      this.$dialog.confirm({text: this.$t('are-you-sure')}).then(result => {
        if(result) {
          this.loading = true
          SubscriptionRepository.delete(item.id).then(response => {
            this.$dialog.notify.success(this.$t('update-success'))
            this.loadData()
          }).catch(error => {
            this.notifyError(error)
            this.loading = false
          })
        }
      })
    },

    openSyncProformaInvoiceModal() {
      this.showSyncProformaInvoiceModal = true
      this.deleteNotInvoicedProformaInvoice = false
      this.syncProformaMode = 'from-month'
      this.syncProformaInvoiceFromMonth = dayjs().subtract(1, 'month').format('YYYY-MM-DD')
    },

    loadLastSyncTask() {
      ProformaInvoiceSyncTaskRepository.search({organizationId: this.organization.id, limit: 1}, 'organization-last-sync').then(result => {
        this.lastSyncTask = (result.items || [])[0] || null
      }).catch(() => {
        this.lastSyncTask = null
      })
    },

    syncTaskStatusColor(status) {
      switch (status) {
        case 'DONE': return 'success'
        case 'FAILED': return 'error'
        case 'IN_PROGRESS': return 'primary'
        default: return 'grey'
      }
    },

    syncProformas() {
      if(this.syncProformaLoading) {
        return
      }
      this.syncProformaLoading = true
      let month = null
      if(this.syncProformaMode === 'from-month' && this.syncProformaInvoiceFromMonth) {
        month = dayjs(this.syncProformaInvoiceFromMonth).format('YYYY-MM')
      }
      // Elle tourne sur un serveur : on suit sa progression (ou celle déjà en cours pour cette
      // organisation, que le serveur renvoie au lieu d'en lancer une seconde).
      ProformaInvoiceRepository.syncOrganization(this.organization.id, month, this.deleteNotInvoicedProformaInvoice).then(data => {
        this.syncProformaLoading = false
        this.showSyncProformaInvoiceModal = false
        this.$refs.syncTaskModal.open(data.taskId)
        this.loadLastSyncTask()
      }).catch(err => {
        this.syncProformaLoading = false;
         this.notifyError(err)
      })
    },
  },

}
</script>

<style lang="scss">
.subscriptions-search {

}
</style>
